import { useState } from 'react';

export default function ExportServices() {
  const scrollToInquiryForm = () => {
    const inquirySection = document.getElementById('product-inquiry');
    if (inquirySection) {
      inquirySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const exportProducts = [
    {
      product: 'BSF Protein Meal',
      badge: null,
      markets: ['Europe', 'Asia', 'North America'],
      specifications: 'Min. 60% protein, Export grade quality',
      packaging: '25kg bags, Bulk containers',
      certifications: ['HACCP', 'ISO 22000', 'Organic']
    },
    {
      product: 'Dried BSF Larvae',
      badge: null,
      markets: ['Pet Food Industry', 'Aquaculture', 'Poultry'],
      specifications: 'Whole dried larvae, Premium quality',
      packaging: '10kg, 25kg bags',
      certifications: ['Feed Grade', 'Quality Assured']
    },
    {
      product: 'BSF Oil',
      badge: null,
      markets: ['Cosmetics', 'Feed Industry', 'Biodiesel'],
      specifications: 'Cold-pressed, High purity',
      packaging: 'Drums, IBC containers',
      certifications: ['Food Grade', 'Cosmetic Grade']
    },
    {
      product: 'Pure Moringa Powder',
      badge: 'New',
      markets: ['Health & Wellness', 'Europe', 'West Africa'],
      specifications: '100% pure, low-heat processed, no additives or fillers',
      packaging: '50g, 100g, 250g, 1kg retail packs · Bulk wholesale available',
      certifications: ['100% Natural', 'No Additives', 'Ghana Origin']
    }
  ];

  const exportProcess = [
    {
      step: '1',
      title: 'Quality Assessment',
      description: 'Product quality verification and certification',
      icon: 'ri-shield-check-line'
    },
    {
      step: '2',
      title: 'Documentation',
      description: 'Export permits and compliance documentation',
      icon: 'ri-file-text-line'
    },
    {
      step: '3',
      title: 'Packaging & Labeling',
      description: 'Export-standard packaging and labeling',
      icon: 'ri-box-3-line'
    },
    {
      step: '4',
      title: 'Logistics & Shipping',
      description: 'International shipping and delivery coordination',
      icon: 'ri-ship-line'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Export Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connecting Ghanaian BSF products and pure moringa powder to international markets with full export support and compliance assistance.
          </p>
        </div>

        {/* Export Products */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Export Product Portfolio
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {exportProducts.map((product, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow relative">
                {product.badge && (
                  <span className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                    {product.badge}
                  </span>
                )}
                <h4 className="text-lg font-semibold text-gray-900 mb-4 pr-10">
                  {product.product}
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700 text-sm">Target Markets:</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {product.markets.map((market, marketIndex) => (
                        <span key={marketIndex} className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                          {market}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700 text-sm">Specifications:</span>
                    <p className="text-gray-600 text-sm mt-1">{product.specifications}</p>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700 text-sm">Packaging:</span>
                    <p className="text-gray-600 text-sm mt-1">{product.packaging}</p>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700 text-sm">Certifications:</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {product.certifications.map((cert, certIndex) => (
                        <span key={certIndex} className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <button 
                  onClick={scrollToInquiryForm}
                  className="w-full mt-5 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Request Export Quote
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Export Process */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Our Export Process
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {exportProcess.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${step.icon} text-2xl text-green-600`}></i>
                </div>
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                  {step.step}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {step.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Export Capabilities */}
        <div className="bg-gradient-to-r from-green-700 to-green-500 rounded-xl p-8 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                Export Capabilities &amp; Partnerships
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center">
                  <i className="ri-check-line mr-3"></i>
                  Monthly export capacity: 50+ tons
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3"></i>
                  Pure moringa powder available in retail &amp; bulk export packs
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3"></i>
                  Established logistics partnerships
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3"></i>
                  International quality certifications
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3"></i>
                  Competitive pricing and terms
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3"></i>
                  Full documentation support
                </li>
              </ul>
              <div className="flex gap-4">
                <button
                  onClick={scrollToInquiryForm}
                  className="bg-white text-green-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Become Export Partner
                </button>
                <button
                  onClick={scrollToInquiryForm}
                  className="border-2 border-white text-white hover:bg-white hover:text-green-700 px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Request Catalog
                </button>
              </div>
            </div>
            <div className="h-64 bg-white/10 rounded-lg overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/515f1d4b41b523b332d9ee04f0f10bfc.jpg"
                alt="Export Services — BSF and Moringa Powder"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
